  import React, { useEffect, useRef, useState } from "react";
  import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
  import { motion } from "framer-motion";
  import LandingPage from './LandingPage';
  import RegistrationPage from './RegistrationPage';
  import CustomScenarioPage from './CustomScenarioPage';
  import TranscriptPage from './TranscriptPage';
  import VerifyEmail from './VerifyEmail';
  import ResetPassword from "./ResetPassword";
  import UserDashboard from './UserDashboard';
  import { API_BASE_URL } from "./config";
  import {
    BRAND, 
    contrastText, 
    getThemeColors, 
    scrollbarStyles, 
    spinnerKeyframes, 
    autofillStyles, 
    cardStyle, 
    sectionHeaderStyle, 
    sendBtnStyle,
    chatBubbleStyle,
    LANDING_BG
  } from "./themes";

  function Spinner({ size = 14, color = '#fff' }) {
    return (
      <>
        <div style={{
          width: size,
          height: size,
          border: `2px solid ${color}`,
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite',
          display: 'inline-block',
          marginRight: 6
        }} />
      </>
    );
  }

  export function useIsMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth < 768);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    return isMobile;
  }

  function parsePersonaData(backendData) {
    const { background, scenario } = backendData;
    
    const name = background.name || "";
    const role = background.position || "";
    const dept = scenario.team_name || "";
    
    return {
      scenario_name_full: scenario.scenario_name || "",
      scenario_num: scenario.scenario_num || "",
      scenario: scenario.scenario_name || "",
      industry: scenario.industry || "",
      company_type: scenario.company_type || "",
      team_name: scenario.team_name || "",
      team_size: scenario.team_composition || "",
      user_role: scenario.manager_role || "",
      name: name,
      role: role,
      dept: dept,
      persona_description: background,
      recent_performance_issues: scenario.recent_performance || [],
      recent_events_and_metrics: scenario.recent_events_and_metrics || [],
      further_work_characteristics: scenario.further_work_characteristics_and_persona_details || [],
      recent_event: scenario.recent_event || null,
      goals: scenario.feedback_goals || scenario.mentor_opportunities || [],
      // mentoring-specific fields
      archetype: scenario.archetype || "",
      tenure: scenario.tenure || "",
      strengths: scenario.strengths || "",
      challenge_pattern: scenario.challenge_pattern || "",
      blind_spot: scenario.blind_spot || "",
      emotional_tone: scenario.emotional_tone || "",
    };
  }

  const styles = {
    page: {
      minHeight: '100vh',
      backgroundColor: BRAND.canvas,
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
      display: 'flex',
      flexDirection: 'column',
    },
    container: {
      maxWidth: 1500,
      margin: '0 auto',
      width: '100%',
      padding: 24,
      boxSizing: 'border-box',
      flex: 1,
    },
    topBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: 64,
      padding: '0 20px',
      backgroundColor: 'rgba(34,40,49,0.95)',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    },
    brand: { display: 'flex', alignItems: 'center', gap: 12 },
    flexCenter: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    logoBox: (size = 40, theme = 'dark') => ({ 
      width: size, 
      height: size, 
      borderRadius: 8,
      filter: theme === 'dark' ? 'invert(1) brightness(1.2)' : 'none',
      objectFit: 'contain'
    }),
    grid: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, alignItems: 'start' },
    sendBtn: sendBtnStyle,
    footer: { borderTop: '1px solid rgba(255,255,255,0.05)', padding: '14px 20px', fontSize: 12, color: '#bfc5cc' },
    chatText: { 
      fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
      fontSize: 14,
      lineHeight: '20px'
    },
    card: cardStyle,
    divider: (themeColors) => ({
      border: 'none',
      height: 1,
      background: themeColors.borderColor,
      margin: '12px 0'
    }),
    avatar: (size = 44, bgColor = BRAND.primary) => ({
      width: size,
      height: size,
      borderRadius: 10,
      background: bgColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 700
    }),
    sectionHeader: sectionHeaderStyle,
    topBarBg: (theme) => theme === 'dark' ? 'rgba(34,40,49,0.95)' : 'rgba(255,255,255,0.95)'
  };

  function ChatBubble({ from, text, time, isLatest, onTypingComplete, streamingDone = true, theme, isMobile, themeColors }) {
    const isUser = from === 'user';
    const [displayedText, setDisplayedText] = useState(isLatest && !isUser ? '' : text);
    const textRef = useRef(text);
    const doneRef = useRef(streamingDone);

    useEffect (() => { textRef.current = text; }, [text]);
    useEffect (() => {doneRef.current = streamingDone; }, [streamingDone]);

    useEffect(() => {
      if (isLatest && !isUser) {
        let index = 0;
        const timer = setInterval(() => {
          const current = textRef.current;
          if (index < current.length) {
            setDisplayedText(current.slice(0, index + 1));
            index++;
          } else if (doneRef.current) {
            clearInterval(timer);
            if (onTypingComplete) onTypingComplete();
          }
        }, 15);
        return () => clearInterval(timer);
      }
    }, []);

    useEffect(() => {
      if (!isLatest && !isUser) {
        setDisplayedText(text);
      }
    }, [text, isLatest]);

    const alignStyle = { 
      display: 'flex', 
      justifyContent: isUser ? 'flex-end' : 'flex-start', 
      marginBottom: 8,
    };

    const bubbleStyle = chatBubbleStyle(isUser, theme, themeColors);

    return (
      <div style={alignStyle}>
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }} style={bubbleStyle}>
          <div style={{ ...styles.chatText, whiteSpace: 'pre-line', color: 'inherit' }}>{displayedText}</div>
          {time && <div style={{ marginTop: 6, fontSize: 11, color: isUser ? 'rgba(255,255,255,0.6)' : (theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)') }}>{time}</div>}
        </motion.div>
      </div>
    );
  }

  function BrandWordmark({theme, compact = false, showEval = false}) {
    const themeColors = getThemeColors(theme)
    const isMobile = useIsMobile()
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* When logo would be horizontal (symbol to left of text), use wordmark */}
        {(window.innerWidth > 420 || !showEval) ? (
          <img 
            src="/logo_wordmark.png" 
            alt="Loopen" 
            style={{ 
              height: isMobile? 32:36,
              width: 'auto',
              objectFit: 'contain',
              filter: theme === 'dark' ? 'invert(1) brightness(1.2)' : 'none',
              paddingLeft: isMobile? 3:0
            }} 
          />
        ) : (
          // Mobile Eval pane: symbol above text (vertical arrangement)
          <>
            <img src="/logo_symbol.png" alt="Loopen" style={styles.logoBox(48, theme)} />
  {/*          <div style={{
              fontSize: 11,
              fontWeight: 700,
              color: themeColors.text,
              lineHeight: 1,
              marginTop: -10,
            }}>
              loopen
            </div>*/}
          </>
        )}
        {/*don't display byline in mobile view*/}
        {!compact && <div style={{ 
          fontSize: 11, 
          color: themeColors.textMuted,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
          lineHeight: 1,
        }}>
          by Lausanne Business Solutions
        </div>}
      </div>
    );
  }

  export function ThemeToggle({ theme, setTheme }) {
    return (
      <div 
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        style={{
          width: 60,
          height: 30,
          borderRadius: 15,
          background: theme === 'dark' ? BRAND.primary : BRAND.accent,
          position: 'relative',
          cursor: 'pointer',
          transition: 'background 0.3s ease',
          border: '2px solid rgba(255,255,255,0.2)',
          boxSizing: 'border-box'
        }}
      >
        <div style={{
          position: 'absolute',
          top: 3,
          left: theme === 'dark' ? 3 : 31,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12
        }}>
          {theme === 'dark' ? '🌙' : '☀️'}
        </div>
      </div>
    );
  }

  function MarkdownRenderer({ markdown, theme }) {
    const themeColors = getThemeColors(theme);

    const cleanedMarkdown = markdown.replace(/```[\s\S]*?\n/g, '').replace(/```\s*$/g, '');
    const lines = cleanedMarkdown.split('\n');
    
    const parseBold = (text) => {
      const parts = [];
      let remaining = text;
      let key = 0;
      
      while (remaining.length > 0) {
        const start = remaining.indexOf('**');
        if (start === -1) {
          parts.push(<span key={key++}>{remaining}</span>);
          break;
        }
        
        if (start > 0) {
          parts.push(<span key={key++}>{remaining.substring(0, start)}</span>);
        }
        
        const end = remaining.indexOf('**', start + 2);
        if (end === -1) {
          parts.push(<span key={key++}>{remaining}</span>);
          break;
        }
        
        parts.push(<strong key={key++} style={{ fontWeight: 700, color: themeColors.text }}>{remaining.substring(start + 2, end)}</strong>);
        remaining = remaining.substring(end + 2);
      }
      
      return parts;
    };

    return (
      <div style={{ fontSize: 14, color: themeColors.textSecondary }}>
        {lines.map((line, i) => {
          if (line.startsWith('# ')) {
            return <h1 key={i} style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, marginTop: 0, color: themeColors.text }}>{line.substring(2)}</h1>;
          }
          
          if (line.startsWith('## ')) {
            return <h2 key={i} style={{ fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 12, color: BRAND.accent }}>{line.substring(3)}</h2>;
          }
          
          if (line.trim() === '') {
            return <div key={i} style={{ height: 8 }} />;
          }
          
          return <div key={i} style={{ marginBottom: 8, lineHeight: '1.8' }}>{line.includes('**') ? parseBold(line) : line}</div>;
        })}
      </div>
    );
  }

  function ScrollableCard({ title, children, theme, height = '100%', innerStyle = {} }) {
    const themeColors = getThemeColors(theme);
    
    return (
      <div style={{
        ...styles.card(themeColors),
        height: height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: 20, fontWeight: 700, color: themeColors.text, flexShrink: 0 }}>
          {title}
        </h2>
        <div className="custom-scrollbar" style={{ 
          flex: 1,
          overflowY: 'auto', 
          paddingRight: 8,
          ...innerStyle
        }}>
          {children}
        </div>
      </div>
    );
  }

  function Modal({ onClose, title, subtitle, children, theme, showHeader = true }) {
    const isMobile = useIsMobile();
    const themeColors = getThemeColors(theme);
    
    return (
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        background: 'rgba(0,0,0,0.5)', 
        ...styles.flexCenter,
        zIndex: 1000,
        padding: 20,
        backdropFilter: 'blur(3px)',
        fontFamily: BRAND.font
      }} onClick={onClose}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          style={{ 
            ...styles.card(themeColors),
            padding: 0,
            maxWidth: isMobile ? '90vw' : 800, 
            maxHeight: '85vh', 
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
            border: `1px solid ${themeColors.borderColor}`
          }} 
          onClick={(e) => e.stopPropagation()}
        >
        {showHeader && (
          <div style={{ 
            padding: '24px 28px', 
            borderBottom: `1px solid ${themeColors.borderColor}`,
            position: 'sticky',
            top: 0,
            background: themeColors.cardBg,
            backdropFilter: 'blur(10px)',
            zIndex: 10
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: themeColors.text }}>
                  {title}
                </h2>
                {subtitle && <div style={{ fontSize: 14, color: themeColors.textMuted, marginTop: 6 }}>{subtitle}</div>}
              </div>
              <button 
                onClick={onClose} 
                style={{ 
                  ...styles.sendBtn, 
                  background: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                  padding: '8px 14px',
                  fontSize: 16,
                  fontWeight: 700,
                  color: themeColors.textSecondary,
                  border: `1px solid ${themeColors.borderColor}`,
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}
          <div style={showHeader ? 
            { padding: '24px 28px 28px 28px' } :
            {
              padding: 0,
              height: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }
          }>
            {children}
          </div>
        </motion.div>
      </div>
    );
  }

  function ScenarioDropdown({ onScenarioSelected, theme, dropdownRef, apiBase = API_BASE_URL, mode='feedback' }) {
    const [scenarios, setScenarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const isMobile = useIsMobile();
    const themeColors = getThemeColors(theme);
    const navigate = useNavigate();

    useEffect(() => {
      const fetchScenarios = async () => {
        try {
          const res = await fetch(`${apiBase}/scenarios`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
          });
          if (res.status === 401) {
          navigate("/login");            // or setShowLanding(true) / open login modal
          return;                        // stop here
          };
          const data = await res.json();
          setScenarios(data.scenarios);
        } catch (err) {
          console.error("Error fetching scenarios:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchScenarios();
    }, []);

    return (
      <div 
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: 80,
          right: isMobile ? 10 : 20,
          left: isMobile ? 10 : 'auto',
          marginTop: 4,
          width: isMobile ? 'auto' : 400,
          maxHeight: '60vh',
          background: themeColors.cardBg,
          border: `1px solid ${themeColors.borderColor}`,
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
          zIndex: 1000,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: BRAND.font
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: themeColors.text }}>
            <Spinner size={20} color={themeColors.text} />
            <div style={{ marginTop: 8, fontSize: 13 }}>Loading...</div>
          </div>
        ) : (
          <>
            <div 
              onClick={() => onScenarioSelected(null)}
              style={{
                padding: 12,
                background: themeColors.primary,
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                borderBottom: `1px solid ${themeColors.borderColor}`
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              🎲 Random Scenario
            </div>
            
            <div className="custom-scrollbar" style={{ 
              flex: 1, 
              overflowY: 'auto',
              padding: 4
            }}>
              {scenarios.map((scenario, idx) => (
                <div
                  key={idx}
                  onClick={() => onScenarioSelected(scenario.idx)}
                  style={{
                    padding: 10,
                    margin: 4,
                    background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13,
                    color: themeColors.text,
                    transition: 'background 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)';
                  }}
                >
                  {idx + 1}: {scenario.name} - {scenario.scenario_name}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  function getInitials(name) {
    if (!name) return "";
    const titles = /^(Dr|Mr|Mrs|Ms|Prof)\.?$/i;
    return name
      .replace(/["'“][^"'“”]*["'”]/g, "")  // Remove nicknames
      .split(/\s+/)              // Split on whitespace
      .filter(part => part && !titles.test(part))
      .map(part => part[0].toUpperCase())
      .join("");
  }

  function AuthScreen({ theme, setTheme }) {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [authError, setAuthError] = useState('');
    const themeColors = getThemeColors(theme);

    //forget password
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotStatus, setForgotStatus] = useState(""); // success/info message
    const [forgotError, setForgotError] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);

    return (
      <>
        <style>{autofillStyles(theme)}</style>
        <div style={{ 
          padding: 28, 
          background: themeColors.background, 
          minHeight: '100vh',
          fontFamily: BRAND.font 
        }}>
          <div style={{ padding: 28, background: themeColors.background, minHeight: '100vh' }}>
            <div style={{ marginBottom: 18 }}>
              <img 
                src="/logo_wordmark.png" 
                alt="Loopen" 
                style={{ 
                  height: 48,
                  width: 'auto',
                  objectFit: 'contain',
                  filter: theme === 'dark' ? 'invert(1) brightness(1.2)' : 'none'
                }} 
              />
              <div style={{ fontSize: 11, color: '#9aa4ad', marginTop: 4 }}>by Lausanne Business Solutions</div>
            </div>

            <div style={{ maxWidth: 760 }}>
              <div style={{ 
                background: themeColors.cardBg,
                borderRadius: 10,
                padding: 16,
                boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
                border: `1px solid ${themeColors.borderColor}`
              }}>
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, background: BRAND.accent }} />
                  <div style={{ fontSize: 18, fontWeight: 700, color: themeColors.text }}>
                    Log in to test your feedback skills
                  </div>
                </div>

                <form onSubmit={async (e) => { 
                  e.preventDefault();
                  
                  const username = e.target.querySelector('input[type="text"]').value;
                  const password = e.target.querySelector('input[type="password"]').value;

                  try {
                    const res = await fetch(`${API_BASE_URL}/login`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      credentials: "include",
                      body: JSON.stringify({ username, password })
                    });
                    
                    const data = await res.json();
                    
                    if (data.success) {
                      localStorage.setItem("auth_session", data.session_id);
                      localStorage.setItem("username", username);
                      localStorage.setItem("remaining_turns", data.RemainingTurns);
                      navigate('/userdashboard');
                    } else {
                      setAuthError(data.error || "Authentication failed");
                    }
                  } catch (err) {
                    setAuthError("Connection error. Is the server running?");
                  }
                }}>
                  <div style={{ 
                    display: 'flex', 
                    gap: 12, 
                    marginTop: 12, 
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: 6, color: themeColors.text, fontWeight: 600 }}>
                        Username
                      </label>
                      <input 
                        type="text" 
                        placeholder="username" 
                        required 
                        onChange={() => setAuthError('')}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          borderRadius: 8,
                          background: themeColors.inputBg,
                          color: themeColors.text,
                          border: `1px solid ${themeColors.borderColor}`,
                          width: '100%',
                          boxSizing: 'border-box',
                          fontSize: 16
                        }} 
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: 6, color: themeColors.text, fontWeight: 600 }}>
                        Password
                      </label>
                      <input 
                        type="password" 
                        placeholder="••••••••" 
                        required 
                        onChange={() => setAuthError('')}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          borderRadius: 8,
                          background: themeColors.inputBg,
                          color: themeColors.text,
                          border: `1px solid ${themeColors.borderColor}`,
                          width: '100%',
                          boxSizing: 'border-box',
                          fontSize: 16
                        }} 
                      />
                      {/* forget password link */}
                      <div style={{ marginTop: 8 }}> 
                        <button
                          type="button"
                          onClick={() => {
                            setShowForgot(true);
                            setForgotStatus("");
                            setForgotError("");
                            setForgotEmail("");
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            padding: 0,
                            color: themeColors.primary,
                            cursor: "pointer",
                            fontSize: 13,
                            textDecoration: "underline",
                            textAlign: "left",
                          }}
                        >
                          Forgot password?
                        </button>
                      </div>
                    </div>
                  </div>

                  {authError && (
                    <div style={{
                      color: BRAND.error,
                      fontSize: 14,
                      marginTop: 8,
                      fontWeight: 500
                    }}>
                      {authError}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, alignItems: 'center' }}>
                    <button 
                      type="submit"
                      style={{ ...styles.sendBtn }}
                    >
                      Sign In
                    </button>
                  </div>

  {/* forget password link */}
  {showForgot && (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 16,
      }}
      onClick={() => setShowForgot(false)}
    >
      <div
        style={{
          width: "min(520px, 100%)",
          background: themeColors.inputBg,
          border: `1px solid ${themeColors.borderColor}`,
          borderRadius: 12,
          padding: 18,
          boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
          color: themeColors.text,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Reset your password</div>
          <button
            type="button"
            onClick={() => setShowForgot(false)}
            style={{
              background: "transparent",
              border: "none",
              color: themeColors.text,
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div style={{ marginTop: 10, fontSize: 14, opacity: 0.9 }}>
          Enter the email for your account. We’ll send a reset link if it exists.
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Email / Username
          </label>
          <input
            type="text"
            value={forgotEmail}
            onChange={(e) => {
              setForgotEmail(e.target.value);
              setForgotStatus("");
              setForgotError("");
            }}
            placeholder="you@example.com"
            style={{
              padding: "10px 12px",
              borderRadius: 8,
              background: themeColors.inputBg,
              color: themeColors.text,
              border: `1px solid ${themeColors.borderColor}`,
              width: "100%",
              boxSizing: "border-box",
              fontSize: 16,
            }}
          />
        </div>

        {forgotError && (
          <div style={{ color: BRAND.error, fontSize: 13, marginTop: 10, fontWeight: 500 }}>
            {forgotError}
          </div>
        )}

        {forgotStatus && (
          <div style={{ color: themeColors.text, fontSize: 13, marginTop: 10, fontWeight: 500, opacity: 0.95 }}>
            {forgotStatus}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button
            type="button"
            onClick={() => setShowForgot(false)}
            style={{
              background: "transparent",
              border: `1px solid ${themeColors.borderColor}`,
              color: themeColors.text,
              borderRadius: 10,
              padding: "10px 14px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={forgotLoading}
            onClick={async () => {
              const emailOrUser = forgotEmail.trim();
              if (!emailOrUser) {
                setForgotError("Please enter your email/username.");
                return;
              }

              setForgotLoading(true);
              setForgotError("");
              setForgotStatus("");

              try {
                const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email_or_username: emailOrUser }),
                });

                // Even if the account doesn't exist, we still show a generic success message
                // to avoid account enumeration.
                let data = null;
                try { data = await res.json(); } catch (_) {}

                if (res.ok) {
                  setForgotStatus("If an account exists, a reset link has been sent.");
                } else {
                  setForgotStatus("If an account exists, a reset link has been sent.");
                  // Optional: if you want to show server error for debugging:
                  // setForgotError((data && data.error) ? data.error : "Request failed");
                }
              } catch (err) {
                setForgotError("Connection error. Please try again.");
              } finally {
                setForgotLoading(false);
              }
            }}
            style={{
              ...styles.sendBtn,
              opacity: forgotLoading ? 0.7 : 1,
              cursor: forgotLoading ? "not-allowed" : "pointer",
            }}
          >
            {forgotLoading ? "Sending..." : "Send reset link"}
          </button>
        </div>
      </div>
    </div>
  )}



                </form> 
              </div> 

              <div style={{ 
                marginTop: 20, 
                ...styles.flexCenter,
                gap: 12
              }}>
                <ThemeToggle theme={theme} setTheme={setTheme} />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  function TopBar({ roleplayName, onEvaluate, onRestartScenario, onNewScenario, employee, onShowPersona, isMobile, onToggleView, theme, loadingEval, setTheme, onMyAccount }) {
    const themeColors = getThemeColors(theme);
    const bot_inits = getInitials(employee.name);
    const user_inits = getInitials(roleplayName || "Herbert Hoover");

    return (
      <div style={{
        ...styles.topBar,
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        height: isMobile ? 'auto' : 64,
        padding: isMobile ? '12px' : '0 20px',
        gap: isMobile ? 8 : 32,
        background: styles.topBarBg(theme),
        borderBottom: `1px solid ${themeColors.borderColor}`
      }}>
        {isMobile ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            width: '100%'
          }}>
            <BrandWordmark theme={theme} compact={isMobile} />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button 
                style={{ 
                  ...styles.sendBtn, 
                  background: themeColors.primary,
                  color: contrastText(themeColors.primary),
                  padding: '6px 8px',
                  fontSize: 13
                }} 
                onClick={onRestartScenario}
              >
                Restart
              </button>
              <button 
                style={{ 
                  ...styles.sendBtn, 
                  background: BRAND.spotlight,
                  color: contrastText(BRAND.spotlight),
                  padding: '6px 8px',
                  fontSize: 13
                }} 
                onClick={onNewScenario}
              >
                New
              </button>
              <button 
                style={{ 
                  ...styles.sendBtn, 
                  background: themeColors.buttonInactive,
                  color: contrastText(themeColors.buttonInactive),
                  padding: '6px 8px',
                  fontSize: 13
                }} 
                onClick={onMyAccount}
              >
                Account
              </button>
              <ThemeToggle theme={theme} setTheme={setTheme} />
            </div>
          </div>

        ) : (
          <BrandWordmark theme={theme} compact={isMobile} />
        )}

        {isMobile && (
          <div style={{
            fontSize: 12,
            color: themeColors.textMuted,
            textAlign: 'center',
            width: '100%',
            marginTop: 4
          }}>
            Welcome, {localStorage.getItem('username')}! You have {localStorage.getItem('remaining_turns')||'-'} monthly messages remaining.
          </div>
        )}
        
        {isMobile && (
          <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 8 }}>
            <button 
              onClick={() => onToggleView('persona')}
              style={{ 
                ...styles.sendBtn, 
                flex: 1,
                background: BRAND.accent
              }}
            >
              Persona
            </button>
            <button 
              onClick={() => onToggleView('scenario')}
              style={{ 
                ...styles.sendBtn, 
                flex: 1,
                background: BRAND.accent
              }}
            >
              Scenario
            </button>
          </div>
        )}
        
        {!isMobile && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 32
          }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={styles.avatar(38, BRAND.spotlight)}>{user_inits}</div>
              <div
                style={{ 
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: themeColors.text }}>{roleplayName || "Herbert Hoover"}</div>
                <div style={{ fontSize: 12, color: themeColors.textMuted }}>{employee.user_role}</div>
              </div>
            </div>
            
            <div style={{ width: 1, height: 40, background: themeColors.borderColor }} />
            
            <div 
              style={{ display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer' }}
              onClick={onShowPersona}
            >
              <div style={styles.avatar(38, themeColors.primary)}>{bot_inits}</div>
              <div
                style={{ 
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: themeColors.text }}>{employee.name}</div>
                <div style={{ fontSize: 12, color: themeColors.textMuted }}>{employee.role} {employee.dept && "•"} {employee.dept}</div>
              </div>
            </div>
            
            <div style={{ width: 1, height: 40, background: themeColors.borderColor }} />
            
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button 
                style={{ 
                  ...styles.sendBtn, 
                  background: themeColors.primary,
                  color: contrastText(themeColors.primary),
                  padding: '8px 12px',
                  fontSize: 13
                }} 
                onClick={onRestartScenario}
              >
                Restart
              </button>
              <button 
                style={{ 
                  ...styles.sendBtn, 
                  background: BRAND.spotlight,
                  color: contrastText(BRAND.spotlight),
                  padding: '8px 12px',
                  fontSize: 13
                }} 
                onClick={onNewScenario}
              >
                New
              </button>
              <button 
                style={{ 
                  ...styles.sendBtn, 
                  background: themeColors.buttonInactive,
                  color: contrastText(themeColors.buttonInactive),
                  padding: '8px 12px',
                  fontSize: 13
                }} 
                onClick={onMyAccount}
              >
                Account
              </button>
              <div style={{ width: 1, height: 30, background: themeColors.borderColor, marginLeft: 4 }} />
              
              <button 
                style={{ 
                  ...styles.sendBtn, 
                  cursor: loadingEval ? 'wait' : 'pointer',
                  padding: '8px 12px',
                  fontSize: 13
                }} 
                onClick={onEvaluate}
                disabled={loadingEval}
              >
                {loadingEval && <Spinner />}
                {loadingEval ? 'Evaluating...' : 'Submit and Evaluate'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  function PersonaPanel({ employee, theme, mode='feedback' }) {
    const themeColors = getThemeColors(theme);
    const background = employee.persona_description;
    
    if (!background) return null;
    
    return (
      <div style={{ color: themeColors.text }}>
        {background.overview && (
          <>
            <div style={styles.sectionHeader(themeColors)}>Overview</div>
            <div style={{ fontSize: 14, color: themeColors.textSecondary, lineHeight: '1.8', marginBottom: 12, whiteSpace: 'pre-line' }}>
              {background.overview}
            </div>
            <hr style={styles.divider(themeColors)} />
          </>
        )}

        {background.big_five_personality_breakdown && (
          <>
            <div style={styles.sectionHeader(themeColors)}>Personality Traits</div>
            {['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'regulation', 'neuroticism']
              .filter(trait => background.big_five_personality_breakdown[trait])
              .map(trait => {
                const data = background.big_five_personality_breakdown[trait];
                return (
                  <div key={trait} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 14, color: themeColors.text, fontWeight: 600, textTransform: 'capitalize', marginBottom: 4 }}>
                      {trait}: {data.level}
                    </div>
                    {data.facets && Object.entries(data.facets).filter(([_, val]) => val).length > 0 && (
                      <div style={{ paddingLeft: 16, fontSize: 13, color: themeColors.textSecondary }}>
                        {Object.entries(data.facets)
                          .filter(([_, val]) => val)
                          .map(([facet, value]) => (
                            <div key={facet} style={{ marginBottom: 2 }}>
                              • {facet.replace(/_/g, ' ')}: {value}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            <hr style={styles.divider(themeColors)} />
          </>
        )}

        {background.behavioral_feedback_response_profile && (
          <>
            <div style={styles.sectionHeader(themeColors)}>Behavioral Response Profile</div>
            <div style={{ paddingLeft: 16 }}>
              {['overview', 'initial_reaction', 'tendency', 'emotional_regulation', 'resistance_points']
                .map(key => {
                  const value = background.behavioral_feedback_response_profile[key];
                  return value ? (
                    <div key={key} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 14, color: themeColors.text, fontWeight: 600, textTransform: 'capitalize', marginBottom: 4 }}>
                        {key.replace(/_/g, ' ')}:
                      </div>
                      <div style={{ fontSize: 14, color: themeColors.textSecondary, lineHeight: '1.8', whiteSpace: 'pre-line' }}>
                        {value}
                      </div>
                    </div>
                  ) : null;
                })}
            </div>
          </>
        )}
      </div>
    );
  }

  function EmployeePanel({ employee, roleplayName, theme, onClose, mode="feedback" }) {
    const themeColors = getThemeColors(theme);
    
    if (!employee) return <div style={{
      background: themeColors.cardBg,
      borderRadius: 10,
      padding: 16,
      boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
      color: themeColors.text
    }}>Loading...</div>;
    
    return (
      <div style={{ 
        padding: 0,
        color: themeColors.text,
        maxHeight: '100%',
        overflowY: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${themeColors.borderColor}`,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ 
              background: BRAND.accent, 
              color: theme === 'dark' ? '#111' : '#000', 
              padding: '6px 10px', 
              borderRadius: 8, 
              whiteSpace: 'nowrap' 
            }}>
              Scenario {employee.scenario_num}
            </div>
            <div>
              <div style={{ fontWeight: 700, color: themeColors.text }}>{employee.scenario}</div>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                ...styles.sendBtn,
                background: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                padding: '8px 14px',
                fontSize: 16,
                fontWeight: 700,
                color: themeColors.textSecondary,
                border: `1px solid ${themeColors.borderColor}`,
              }}
            >
              x
            </button>
          )}
        </div>

        <div className="custom-scrollbar" style={{
          padding: '16px',
          overflowY: 'auto',
          flex: 1
        }}>
          {employee.goals && employee.goals.length > 0 && (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={styles.sectionHeader(themeColors)}>
                  {mode === 'mentoring' ? 'Mentoring Objectives' : 'Feedback Goals'}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 14 }}>
                  {employee.goals.map((t, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '6px 10px',
                        borderRadius: 10,
                        background:
                          theme === 'dark'
                            ? 'rgba(255,255,255,0.06)'
                            : 'rgba(0,0,0,0.04)',
                        color: themeColors.text,
                      }}
                    >
                      {t}
                    </div>
                  ))}
                </div>
              </div>
              <hr style={styles.divider(themeColors)} />
            </>
          )}

          {mode === 'mentoring' && (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={styles.sectionHeader(themeColors)}>Core Issue</div>
                <div style={{ fontSize: 14, color: themeColors.textSecondary }}>{employee.core_issue}</div>
              </div>
              <hr style={styles.divider(themeColors)} />
            </>
          )}

          {mode === 'mentoring' && (employee.archetype || employee.tenure || employee.strengths || employee.challenge_pattern || employee.blind_spot || employee.emotional_tone) && (
            <>
              <div style={{ fontSize: 14, color: themeColors.textSecondary, lineHeight: '1.8', marginBottom: 12 }}>
                {employee.archetype && <div><strong style={{ color: themeColors.text }}>Archetype:</strong> {employee.archetype}</div>}
                {employee.tenure && <div><strong style={{ color: themeColors.text }}>Tenure:</strong> {employee.tenure}</div>}
                {employee.strengths && <div><strong style={{ color: themeColors.text }}>Strengths:</strong> {employee.strengths}</div>}
                {employee.challenge_pattern && <div><strong style={{ color: themeColors.text }}>Challenge Pattern:</strong> {employee.challenge_pattern}</div>}
                {employee.blind_spot && <div><strong style={{ color: themeColors.text }}>Blind Spot:</strong> {employee.blind_spot}</div>}
                {employee.emotional_tone && <div><strong style={{ color: themeColors.text }}>Emotional Tone:</strong> {employee.emotional_tone}</div>}
              </div>
              <hr style={styles.divider(themeColors)} />
            </>
          )}

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, color: themeColors.textSecondary, lineHeight: '1.8' }}>
              {employee.industry && <div><strong style={{ color: themeColors.text }}>Industry:</strong> {employee.industry}</div>}
              {employee.company_type && <div><strong style={{ color: themeColors.text }}>Company:</strong> {employee.company_type}</div>}
              {employee.team_name && <div><strong style={{ color: themeColors.text }}>Team:</strong> {employee.team_name}{employee.team_size && `, ${employee.team_size}`}</div>}
              <div><strong style={{ color: themeColors.text }}>Your Role:</strong> {roleplayName || "Herbert Hoover"}{employee.user_role && `, ${employee.user_role}`}</div>
            </div>
          </div>

          <hr style={styles.divider(themeColors)} />

          {employee.recent_performance_issues && employee.recent_performance_issues.length > 0 && (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={styles.sectionHeader(themeColors)}>Recent Performance</div>
                <ul style={{ paddingLeft: 18, fontSize: 14, color: themeColors.textSecondary, margin: 0 }}>
                  {employee.recent_performance_issues.map((g, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>{g}</li>
                  ))}
                </ul>
              </div>
              <hr style={styles.divider(themeColors)} />
            </>
          )}

          {employee.further_work_characteristics && employee.further_work_characteristics.length > 0 && (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={styles.sectionHeader(themeColors)}>Further Work Characteristics</div>
                <ul style={{ paddingLeft: 18, fontSize: 14, color: themeColors.textSecondary, margin: 0 }}>
                  {employee.further_work_characteristics.map((g, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>{g}</li>
                  ))}
                </ul>
              </div>
              <hr style={styles.divider(themeColors)} />
            </>
          )}

          {employee.recent_events_and_metrics && employee.recent_events_and_metrics.length > 0 && (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={styles.sectionHeader(themeColors)}>Recent Events and Metrics</div>
                <ul style={{ paddingLeft: 18, fontSize: 14, color: themeColors.textSecondary, margin: 0 }}>
                  {employee.recent_events_and_metrics.map((g, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>{g}</li>
                  ))}
                </ul>
              </div>
              <hr style={styles.divider(themeColors)} />
            </>
          )}

          {employee.recent_event && (
            <div style={{ marginBottom: 0 }}>
              <div style={styles.sectionHeader(themeColors)}>Recent Event</div>
              <div style={{ fontSize: 14, color: themeColors.textSecondary, lineHeight: '1.8', whiteSpace: 'pre-line' }}>              {employee.recent_event}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  function ChatPane({
    onMessagesChange, 
    onShowPersona, 
    employee, 
    initialMessages = [], 
    theme, 
    isMobile, 
    mobileButtonHeight = 0, 
    continuedChat, 
    setRemainingTurns,
    onCreateBranch,
    branchPending,
    onBranchConsumed,
  }) {
    // --- text to voice Setup ---
    const [ttsText, setTtsText] = useState("");
    const [isSpeaking, setIsSpeaking] = useState(false);
    const startSpeaking = () => {
    if (!ttsText) return;

      // stop anything currently playing
      window.speechSynthesis.cancel();

      setIsSpeaking(true);
      speakWithNarration(ttsText);
    };

    const stopSpeaking = () => {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    };


    const [availableVoices, setAvailableVoices] = useState([]);

    // Load all browser voices
    useEffect(() => {
      let tries = 0;

      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();

        if (voices.length > 0) {
          console.log("Voices loaded:", voices.map(v => v.name));
          setAvailableVoices(voices);
          return;
        }

        if (tries < 5) {
          tries++;
          setTimeout(loadVoices, 30);
        } else {
          console.warn("Failed to load voices after 5 tries.");
        }
      };

      window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
      loadVoices();

      return () => {
        window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
      };
    }, []);


    // ------------------
    // Voice Helper: Pick first working voice
    // ------------------
    const findVoice = (preferredNames) => {
      for (const name of preferredNames) {
        const v = availableVoices.find((voice) => voice.name === name);
        if (v) return v;
      }
      return null;
    };


    // ------------------
    // SPEAK A SINGLE SEGMENT
    // ------------------
    const speakSegment = (text, preferredVoices) => {
      const utter = new SpeechSynthesisUtterance(text);
      const voice = findVoice(preferredVoices);

      if (voice) {
        utter.voice = voice;
      } else {
        console.warn("No preferred voices found — using default.");
      }

      utter.lang = "en-US";
      window.speechSynthesis.speak(utter);
    };


    // ------------------
    // PARSE *action text*
    // ------------------
    const parseSegments = (text) => {
      const segments = [];
      const regex = /\*([^*]+)\*/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(text)) !== null) {
        const before = text.slice(lastIndex, match.index).trim();
        if (before) segments.push({ type: "dialogue", text: before });

        segments.push({ type: "action", text: match[1].trim() });

        lastIndex = regex.lastIndex;
      }

      const after = text.slice(lastIndex).trim();
      if (after) segments.push({ type: "dialogue", text: after });

      return segments;
    };


    // ------------------
    // SPEAK FULL TEXT WITH TWO VOICES
    // ------------------
    const speakWithNarration = (fullText) => {
      if (availableVoices.length === 0) {
        console.warn("Voices not loaded yet — retrying...");
        setTimeout(() => speakWithNarration(fullText), 300);
        return;
      }

      const segments = parseSegments(fullText);

      segments.forEach((seg) => {
        if (seg.type === "dialogue") {
          speakSegment(seg.text, [
            "Google UK English Female",    // Android
            "Google UK English",           // Desktop
            "Samantha",                    // macOS
            "Microsoft Hazel Desktop"      // Windows
          ]);
        } else {
          //speakSegment(`Narration: ${seg.text}`, [
          speakSegment(`${seg.text}`, [
            "Google UK English Male",
            "Google US English",           // Chrome default
            "Microsoft David Desktop",     // Windows
            "Alex"                         // macOS
          ]);
        }
      });
    };
    // --- end TTS setup ---



    // --- Start Speech Recognition Setup ---
    const lastResultIndexRef = useRef(0);


    const [isListening, setIsListening] = useState(false);

    const recognitionRef = useRef(null);

    // Initialize recognition ONCE
    useEffect(() => {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        console.warn("Speech Recognition not supported.");
        return;
      }

      const recog = new SpeechRecognition();
      recog.lang = "en-US";
      recog.continuous = true;       // allow long-form listening
      recog.interimResults = false;

      recognitionRef.current = recog;

      return () => {
        // cleanup if component unmounts
        try {
          recog.stop();
        } catch {}
      };
    }, []);

    // --- Start Listening ---
    const startListening = () => {
      const recognition = recognitionRef.current;
      if (!recognition) return;

      if (isListening) return; // prevents double starts

      try {
        recognition.start();
      } catch (error) {
        console.warn("Recognition already started");
      }

      recognition.onstart = () => {
        console.log("🎤 Listening...");
        setIsListening(true);
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);

        // Auto-restart for common transient errors
        if (event.error === "no-speech" || event.error === "audio-capture") {
          console.log("Attempting auto-restart...");
          try {
            recognition.start();
          } catch {}
        }

        setIsListening(false);
      };

      recognition.onresult = (event) => {
        let newText = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            newText += event.results[i][0].transcript + " ";
            console.log("Heard:", newText);
          }
        }

        if (newText) {
          setDraft(prev => (prev + " " + newText).trim());
          console.log("Heard:", newText);
        }
      };

    };

    // --- Stop Listening ---
    const stopListening = () => {
      const recognition = recognitionRef.current;
      if (!recognition) return;
      console.log("🛑 Stopping recognition...");
      setIsListening(false);

      try {
        recognition.stop();
      } catch {}
    };

    // --- End Speech Recognition function ---

    const themeColors = getThemeColors(theme);
    const [messages, setMessages] = useState(initialMessages.length > 0 ? initialMessages : []);
    const [draft, setDraft] = useState('');
    const [isLoadingFirstMessage, setIsLoadingFirstMessage] = useState(!continuedChat && initialMessages.length === 0);
    const [isTyping, setIsTyping] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [userGoesFirst, setUserGoesFirst] = useState(false);
    const [isStreamingDone, setIsStreamingDone] = useState(true);
    const [pendingEdit, setPendingEdit] = useState(branchPending);

    // set up animation for branching action
    const [removingFrom, setRemovingFrom] = useState(null);
    const requestBranch = (index) => {
      if (removingFrom !== null) return;
      setRemovingFrom(index);
      setTimeout(() => {
        onCreateBranch(index);
        setRemovingFrom(null);
      }, 320);
    };

    const scrollRef = useRef(null);
    const hasInitialized = useRef(false);
    const bot_inits = employee ? getInitials(employee.name) : "";
    const navigate = useNavigate();

    useEffect(() => {
      if (!hasInitialized.current && !continuedChat && initialMessages.length === 0 && messages.length === 0) {
        hasInitialized.current = true;
        const fetchFirstReply = async () => {
          const sessionId = localStorage.getItem("session_id");
          const username = localStorage.getItem("username");
          console.log("First-reply attempting with session_id:", sessionId);
          if (!sessionId) {
            console.error("No session_id found!");
            return;
          }
          try {
            const resFirst = await fetch(`${API_BASE_URL}/first-reply`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ session_id: sessionId, username: username })
            });
            if (resFirst.status === 401) {
              navigate("/login");            // or setShowLanding(true) / open login modal
              return;                        // stop here
            };



            const firstData = await resFirst.json();
            
            // Check if user goes first
            if (firstData.reply === "USER_FIRST") {
              setMessages([]);
              setIsLoadingFirstMessage(false);
              setUserGoesFirst(true);
            } else {
              // Bot starts - add bot's message
              const now = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
              const firstMsg = { from: 'employee', text: firstData.reply, time: now };
              setMessages([firstMsg]);
              setTtsText(firstData.reply);
              setIsLoadingFirstMessage(false);
              setIsTyping(true);
            }
          } catch (err) {
            console.error("Error fetching first message:", err);
            setIsLoadingFirstMessage(false);
          }
        };
        fetchFirstReply();
      }
    }, []);

    useEffect(() => {
      if (onMessagesChange) onMessagesChange(messages);
    }, [messages]);

    useEffect(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, isTyping]);

    useEffect(() => {
      if (initialMessages.length > 0) {
        setMessages(initialMessages);
      }
    }, [initialMessages.length]);

    const onSend = async () => {
      if (isTyping || isSending) return;
      if (!draft.trim()) return;

      const sessionId = localStorage.getItem("session_id");
          if (!sessionId) {
            console.error("No session_id for chat!");
            return;
          }
       
      const now = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      const userMsg = { from: 'user', text: draft.trim(), time: now, isEdit: pendingEdit };
      setMessages(prev => [...prev, userMsg]);
      if (pendingEdit) {
        setPendingEdit(false);
        if (onBranchConsumed) onBranchConsumed();
      }
      setDraft('');
      setIsSending(true);
      setIsStreamingDone(false);

      try {
        const username = localStorage.getItem("username");
        console.log("username:", username);
        console.log("chat session_id:", sessionId);
        const res = await fetch(`${API_BASE_URL}/chat`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            message: userMsg.text,
            // conversation_history: [...messages, userMsg],
            username: username
          }),
        });
        if (res.status === 401) {
              navigate("/login");            // or setShowLanding(true) / open login modal
              return;                        // stop here
        };

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulated = '';
        let messageSent = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n\n');
          buffer = parts.pop();

          for (const part of parts) {
            if (!part.startsWith('data: ')) continue;
            let parsed;
            try { parsed = JSON.parse(part.slice(6)); }
            catch { continue; }

            if (parsed.chunk) {
              accumulated += parsed.chunk;
              if (!messageSent && accumulated.length >= 100) {
                messageSent = true;
                const employeeMsg = { from: 'employee', text: accumulated, time: now };
                setMessages(prev => [...prev, employeeMsg]);
                setTtsText(accumulated);
                setIsSending(false);
                setIsTyping(true);
              } else if (messageSent) {
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { ...updated[updated.length - 1], text: accumulated };
                  return updated;
                });
              }
            } else if (parsed.done) {
              if (parsed.RemainingTurns !== undefined) {
                localStorage.setItem('remaining_turns', parsed.RemainingTurns);
                setRemainingTurns(parsed.RemainingTurns);
              }
              if (!messageSent) {
                // Response was shorter than the buffer threshold
                const employeeMsg = { from: 'employee', text: accumulated, time: now };
                setMessages(prev => [...prev, employeeMsg]);
                setTtsText(accumulated);
                setIsSending(false);
                setIsTyping(true);
                setIsStreamingDone(true);
                messageSent = true;
              } else {
                const fullText = parsed.reply || accumulated;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { ...updated[updated.length - 1], text: fullText };
                  return updated;
                });
                setTtsText(accumulated);
                setIsStreamingDone(true);
              }
            }
          }
        }
      } catch (err) {
        console.error("Error fetching response:", err);
        setMessages(prev => [
          ...prev, 
          { 
            from: 'employee', text: "⚠️ Error connecting to server", time: now 
          }]);
        setIsSending(false);
        setIsTyping(false);
      }
    };

    return (
    <div style={{ 
      ...styles.card(themeColors),
      paddingBottom: isMobile ? 0 : 16,
      display: 'flex', 
      flexDirection: 'column',
      maxHeight: '100%',
      overflow: 'hidden',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div 
            style={{ display: 'flex', gap: 12, alignItems: 'center', cursor: isMobile ? 'default' : 'pointer' }}
            onClick={isMobile ? undefined : onShowPersona}
          >
            <div style={styles.avatar(44, themeColors.primary)}>{bot_inits}</div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, cursor: 'pointer', color: themeColors.text }}>
                {employee ? employee.name : 'Loading...'}
              </div>
              <div style={{ fontSize: 13, color: themeColors.textMuted }}>
                {employee ? `${employee.role} ${employee.dept && "•"} ${employee.dept}` : ''}
              </div>
            </div>
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          paddingBottom: 10
        }}>
          <div className="custom-scrollbar" style={{ overflowY: 'auto', paddingRight: 8, flex: 1, minHeight: 0 }} ref={scrollRef}>
            {isLoadingFirstMessage && (
              <div style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 8, color: themeColors.textMuted }}>
                <Spinner size={16} color={themeColors.textMuted} />
                Loading conversation...
              </div>
            )}
            {!isLoadingFirstMessage && userGoesFirst && messages.length === 0 && (
              <div style={{ 
                padding: 20, 
                textAlign: 'center', 
                color: themeColors.textMuted,
                fontSize: 14,
                lineHeight: '1.6'
              }}>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: themeColors.text }}>
                  You're starting the conversation
                </div>
                <div>
                  Begin by introducing yourself and the purpose of this meeting
                </div>
              </div>
            )}
            {messages.map((m, i) => {
              const isRemoving = removingFrom !== null && i >= removingFrom;
              return (
                <motion.div
                  key={i}
                  initial={false}
                  animate={{ opacity: isRemoving ? 0 : 1, y: isRemoving ? -16 : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  {onCreateBranch && m.from === 'user' && !(i === messages.length - 1 && (isTyping || !isStreamingDone)) && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 6,
                      marginTop: 8,
                      marginBottom: -6,
                      marginRight: 4,
                      position: 'relative',
                      zIndex: 1,
                    }}>
                      <button
                        onClick={() => requestBranch(i)}
                        title="Retry from this message"
                        style={{
                          padding: '3px 10px',
                          fontSize: 11,
                          fontWeight: 600,
                          lineHeight: 1,
                          borderRadius: 999,
                          background: themeColors.buttonInactive,
                          border: `1px solid ${themeColors.borderColor}`,
                          color: contrastText(themeColors.buttonInactive),
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                        }}
                      >
                        retry
                      </button>
                      {m.isEdit && (
                        <button
                          onClick={() => {}}
                          title="Undo edit and return to the previous branch"
                          style={{
                            padding: '3px 10px',
                            fontSize: 11,
                            fontWeight: 600,
                            lineHeight: 1,
                            borderRadius: 999,
                            background: themeColors.buttonInactive,
                            border: `1px solid ${themeColors.borderColor}`,
                            color: contrastText(themeColors.buttonInactive),
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            cursor: 'pointer',
                          }}
                        >
                          undo edit
                        </button>
                      )}
                    </div>
                  )}
                  <ChatBubble 
                    key={i} 
                    from={m.from} 
                    text={m.text} 
                    time={m.time} 
                    theme={theme}
                    themeColors={themeColors}
                    isMobile={isMobile}
                    isLatest={i === messages.length - 1 && isTyping} 
                    onTypingComplete={() => setIsTyping(false)} 
                    streamingDone={i === messages.length - 1 ? isStreamingDone : true}
                  />
                </motion.div>
              );
            })}
          </div>
          <div style={{ marginTop : isMobile ? 0 : 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type your message here"
                disabled={isTyping}
                style={{ 
                  ...styles.chatText,
                  fontSize: isMobile ? 16 : 14,
                  flex: 1, 
                  padding: '10px 12px', 
                  borderRadius: 8, 
                  background: themeColors.inputBg, 
                  color: themeColors.text, 
                  border: `1px solid ${themeColors.borderColor}`,
                  height: 48, 
                  resize: 'vertical',
                  cursor: isTyping ? 'not-allowed' : 'text',
                  marginBottom: 8
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isTyping) {
                    e.preventDefault();
                    onSend();
                  }
                }}
              />
              <div style={{ display: 'flex', alignItems: 'left', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8}}>
                  <button
                    onClick={isListening ? stopListening : startListening}  
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: BRAND.spotlight,
                      filter: isListening ? 'brightness(1.4)' : 'none',
                      boxShadow: isListening ? `0 0 10px ${BRAND.spotlight}` : 'none',
                      color: contrastText(BRAND.spotlight),
                      border: `1px solid ${themeColors.borderColor}`,
                      cursor: 'pointer',
                      transition: '0.2s ease'
                    }}
                    disabled={isTyping}
                  >
                    {isListening ? '🛑 Stop' : '🎤 Start'}
                  </button>
                  <button
                    onClick={isSpeaking ? stopSpeaking : startSpeaking}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: BRAND.spotlight,
                      filter: isSpeaking ? 'brightness(1.4)' : 'none',
                      boxShadow: isSpeaking ? `0 0 10px ${BRAND.spotlight}` : 'none',
                      color: contrastText(BRAND.spotlight),
                      border: `1px solid ${themeColors.borderColor}`,
                      cursor: 'pointer',
                      transition: '0.2s ease'
                    }}
                    disabled={!ttsText || isTyping}
                  >
                    {isSpeaking ? '🛑 Stop Voice' : '🔊 Play Voice'}
                  </button>  
                </div>
                <button 
                  onClick={onSend} 
                  style={{...styles.sendBtn, cursor: isSending ? 'wait' : 'pointer'}} 
                  disabled={isTyping || isSending}
                >
                  {isSending && <Spinner />}
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function EvaluationPage({ transcript, roleplayName, onReturnToChat, onRestartScenario, onNewScenario, evaluationMarkdown, employee, isMobile, theme, setTheme }) {
    const [showTranscript, setShowTranscript] = useState(false);
    const themeColors = getThemeColors(theme);
    const navigate = useNavigate();
    const evalButtonStyle = {
      ...styles.sendBtn,
      flex: isMobile ? 1 : '0 1 auto',
      minWidth: isMobile ? 'auto' : 0,
      fontSize: isMobile ? 13 : 14,
      padding: isMobile ? '8px 6px' : '10px 10px'
    };

    const handleExport = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/export`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            transcript: transcript,
            evaluation: evaluationMarkdown,
            roleplayName: roleplayName,
            employeeName: employee ? employee.name : 'Employee'
          }),
        });
        const data = await res.json();
        
        // Create and download file
        const blob = new Blob([data.content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const initials = getInitials(employee?.name) || 'XX'; 
        const scenarioNum = employee?.scenario_num || 'X';
        const timestamp = new Date().toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '-T');
        a.download = `loopen-session_${initials}-${scenarioNum}_${timestamp}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Export error:", err);
      }
    };

    return (
      <div style={{
        ...styles.page,
        backgroundColor: themeColors.background,
        color: themeColors.text
      }}>
      <div style={{
        ...styles.topBar,
        flexDirection: isMobile ? 'column' : 'row',
        height: isMobile ? 'auto' : 64,
        padding: isMobile ? '12px' : '0 20px',
        gap: isMobile ? 8 : 0,
        alignItems: isMobile ? 'stretch' : 'center',
        background: styles.topBarBg(theme),
        borderBottom: `1px solid ${themeColors.borderColor}`
      }}>
        {isMobile ? (
          <>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              width: '100%'
            }}>
              <BrandWordmark theme={theme} compact={isMobile} showEval={true}/>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button style={{ ...evalButtonStyle, background: themeColors.primary, color: contrastText(themeColors.primary) }} onClick={onReturnToChat}>
                  Return to Chat
                </button>
                <button style={{ ...evalButtonStyle, background: BRAND.accent }} onClick={onRestartScenario}>
                  Restart Scenario
                </button>
                <button style={{ ...evalButtonStyle, background: themeColors.buttonInactive, color: contrastText(themeColors.buttonInactive) }} onClick={onNewScenario}>
                  New Scenario
                </button>
              </div>
            </div>
            <button onClick={handleExport} style={{ ...evalButtonStyle, background: BRAND.spotlight, color: contrastText(BRAND.spotlight), width: '100%' }}>
              Export Session
            </button>
            <button
              onClick={() => navigate("/userdashboard")}
              style={{ ...evalButtonStyle, background: themeColors.buttonInactive, color: contrastText(themeColors.buttonInactive) }}
            >
              Account
            </button>
          </>
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            position: 'relative'
          }}>
            <div style={{ 
              position: 'absolute',
              left: '16%',
              transform: 'translateX(-50%)'
            }}>
              <BrandWordmark theme={theme} compact={false} showEval={true}/>
            </div>
            
            <div style={{ 
              display: 'flex', 
              gap: 12,
              flexDirection: 'row',
              alignItems: 'center',
              marginLeft: 'auto'
            }}>
              <div style={{
                display: 'flex',
                gap: 8,
                flexDirection: 'row',
                minWidth: 0
              }}>
                <button onClick={onReturnToChat} style={{ ...evalButtonStyle, background: themeColors.primary, color: contrastText(themeColors.primary) }}>
                  Return to Chat
                </button>
                <button onClick={onRestartScenario} style={{ ...evalButtonStyle, background: BRAND.accent }}>
                  Restart Scenario
                </button>
                <button onClick={onNewScenario} style={{ ...evalButtonStyle, background: themeColors.buttonInactive, color: contrastText(themeColors.buttonInactive) }}>
                  New Scenario
                </button>
              </div>
              
              <div style={{ 
                width: 1, 
                height: 40, 
                background: themeColors.borderColor 
              }} />
              <button onClick={handleExport} style={{ ...evalButtonStyle, background: BRAND.spotlight, color: contrastText(BRAND.spotlight), width: '180px' }}>
                Export Session
              </button>
              <div style={{ width: 1, height: 40, background: themeColors.borderColor }} />
              <button
                onClick={() => navigate("/userdashboard")}
                style={{ ...evalButtonStyle, background: themeColors.buttonInactive, color: contrastText(themeColors.buttonInactive) }}
              >
                Account
              </button>
            </div>
          </div>
        )
      }
      </div>

        <div style={styles.container}>
          {isMobile ? (
           <>
             <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
               <button 
                 onClick={() => setShowTranscript(false)} 
                 style={{ 
                   ...styles.sendBtn, 
                   flex: 1,
                   background: !showTranscript ? BRAND.accent : themeColors.buttonInactive,
                   color: !showTranscript ? contrastText(BRAND.accent) : contrastText(themeColors.buttonInactive)
                 }}
               >
                 Evaluation
               </button>
               <button 
                 onClick={() => setShowTranscript(true)} 
                 style={{ 
                   ...styles.sendBtn, 
                   flex: 1,
                   background: showTranscript ? BRAND.accent : themeColors.buttonInactive,
                   color: showTranscript ? contrastText(BRAND.accent) : contrastText(themeColors.buttonInactive)
                 }}
               >
                 Transcript
               </button>
             </div>

            {showTranscript ? (
              <ScrollableCard title="Conversation Transcript" theme={theme} height="70vh" innerStyle={{ display: 'flex', flexDirection: 'column', gap: 12 }}>             
                {transcript.map((m, i) => (
                  <div 
                    key={i} 
                    style={{ 
                      padding: 14, 
                      background: m.from === 'user' 
                        ? (theme === 'dark' ? 'rgba(23, 54, 84, 0.3)' : 'rgba(23, 54, 84, 0.1)') 
                        : (theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'), 
                      borderRadius: 10,
                      borderLeft: `3px solid ${m.from === 'user' ? themeColors.primary : BRAND.accent}`,
                      flexShrink: 0
                    }}
                  >
                    <div style={{ 
                      fontSize: 13, 
                      fontWeight: 700, 
                      color: m.from === 'user' ? BRAND.accent : themeColors.textMuted,
                      marginBottom: 6 
                    }}>
                      {m.from === 'user' ? roleplayName : (employee ? employee.name : 'Employee')}
                    </div>
                    <div style={{ fontSize: 14, color: themeColors.textSecondary, lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                      {m.text}
                    </div>
                  </div>
              ))}
            </ScrollableCard>
          ) : (
            <ScrollableCard title="Coaching Evaluation" theme={theme} height="70vh">
              <MarkdownRenderer markdown={evaluationMarkdown} theme={theme} />
            </ScrollableCard>
          )}
        </>
        ) : (
          // Desktop: two parallel scrolling columns side by side
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 20, height: '78vh' }}>
            {/* Left sidebar - Transcript */}
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <ScrollableCard title="Conversation Transcript" theme={theme} innerStyle={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {transcript.map((m, i) => (
                  <div 
                    key={i} 
                    style={{ 
                      padding: 14, 
                      background: m.from === 'user' 
                        ? (theme === 'dark' ? 'rgba(23, 54, 84, 0.3)' : 'rgba(23, 54, 84, 0.1)') 
                        : (theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'), 
                      borderRadius: 10,
                      borderLeft: `3px solid ${m.from === 'user' ? themeColors.primary : BRAND.accent}`,
                      flexShrink: 0
                    }}
                  >
                    <div style={{ 
                      fontSize: 13, 
                      fontWeight: 700, 
                      color: m.from === 'user' ? BRAND.accent : themeColors.textMuted,
                      marginBottom: 6 
                    }}>
                      {m.from === 'user' ? roleplayName : (employee ? employee.name : 'Employee')}
                    </div>
                    <div style={{ fontSize: 14, color: themeColors.textSecondary, lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                      {m.text}
                    </div>
                  </div>
                ))}
              </ScrollableCard>
            </div>

            {/* Right side - Evaluation */}
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <ScrollableCard title="Coaching Evaluation" theme={theme}>
                <MarkdownRenderer markdown={evaluationMarkdown} theme={theme} />
              </ScrollableCard>
            </div>
          </div>
        )}
      </div>

      <div style={{
        ...styles.footer,
        color: themeColors.textMuted,
        borderTop: `1px solid ${themeColors.borderColor}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>© {new Date().getFullYear()} Lausanne Business Solutions — Loopen. All rights reserved.</span>
        <ThemeToggle theme={theme} setTheme={setTheme} />
      </div>
    </div>
  );}

  function AppShell({ theme, setTheme, apiBase = API_BASE_URL, storageKey='selected_scenario_num', mode='feedback' }) {
    const navigate = useNavigate();
    const location = useLocation();
    const roleplayName = localStorage.getItem('roleplay_name') || '';
    const selectedScenarioNum = localStorage.getItem(storageKey) || null;
    const username = localStorage.getItem("username");
    console.log("username",username)
    const hasInitialized = useRef(false);
    const themeColors = getThemeColors(theme);
    const [employee, setEmployee] = useState(null);
    const [showEval, setShowEval] = useState(false);
    const [transcript, setTranscript] = useState([]);
    const [evaluationMarkdown, setEvaluationMarkdown] = useState('');
    const [loadingEval, setLoadingEval] = useState(false);
    const isMobile = useIsMobile();
    const [modalView, setModalView] = useState(null);
    const evaluateButtonRef = useRef(null);
    const [evaluateButtonHeight, setEvaluateButtonHeight] = useState(0);
    const [continuedChat, setContinuedChat] = useState(false);
    const [restartKey, setRestartKey] = useState(0);
    const [branchPending, setBranchPending] = useState(false);
    const [showScenarioDropdown, setShowScenarioDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const [desktopView, setDesktopView] = useState('scenario');
    const [remainingTurns, setRemainingTurns] = useState(localStorage.getItem('remaining_turns')||'-');
    const lastScenarioSelection = useRef(null);

    const toggleScenarioDropdown = () => setShowScenarioDropdown(!showScenarioDropdown);

    const handleScenarioSelect = (scenarioNum) => {
      handleNewScenario(scenarioNum);
      setShowScenarioDropdown(false);
    };

    const fetchAndSetPersona = async (scenarioNum = undefined) => {
      try {
        const requestScenarioNum = scenarioNum !== undefined ? scenarioNum : selectedScenarioNum;
        lastScenarioSelection.current = requestScenarioNum;
        const username = localStorage.getItem("username");

        let res;
        if(requestScenarioNum !== null && requestScenarioNum !== undefined) {
            res = await fetch(`${apiBase}/persona`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ scenario_num: requestScenarioNum, username: username})
          });
        } else {
          res = await fetch(`${apiBase}/persona`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ username: username})
          });
        }
        if (res.status === 401) {
        navigate("/login");            // or setShowLanding(true) / open login modal
        return;                        // stop here
        };


        const data = await res.json();
        console.log("Raw backend data:", data);
        localStorage.setItem("session_id", data.session_id);
        console.log("persona session_id:", data.session_id);
        
        const parsedEmployee = parsePersonaData(data);
        console.log("Parsed employee data:", parsedEmployee);
        
        setEmployee(parsedEmployee);
      } catch (err) {
        console.error("Error fetching persona:", err);
      }
    };

    useEffect(() => {
      if (isMobile && evaluateButtonRef.current) {
        const height = evaluateButtonRef.current.offsetHeight;
        console.log('Evaluate button height:', height);
        setEvaluateButtonHeight(height);
        }
    }, [isMobile, showEval, employee]);

    // Fetch persona and parse it
    useEffect(() => {
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        const cont = location.state?.continuation;
        if (cont) {
          localStorage.setItem("session_id", cont.sessionId);
          const parsedEmployee = parsePersonaData({ background: cont.background, scenario: cont.scenario });
          setEmployee(parsedEmployee);
          const storedUsername = localStorage.getItem("username");
          const msgs = cont.turns.map(t => ({
            from: t.role === storedUsername ? 'user' : 'employee',
            text: t.content,
            time: '',
          }));
          setTranscript(msgs);
          setContinuedChat(true);
        } else {
          fetchAndSetPersona();
        }
      }
    }, []);

    useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowScenarioDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

    const handleTranscriptUpdate = (msgs) => setTranscript(msgs);

    const handleReturnToChat = () => {
      setShowEval(false);
      setContinuedChat(true);
    };

    const handleRestartScenario = async () => {
        setShowEval(false);
        setTranscript([]);
        await fetchAndSetPersona(lastScenarioSelection.current !== null && lastScenarioSelection.current !== undefined
            ? lastScenarioSelection.current
            : undefined);
        setContinuedChat(false);
        setRestartKey(prev => prev + 1);
        setBranchPending(false);
    };

    const handleNewScenario = async (scenarioNum = undefined) => {
      setEmployee(null);
      await fetchAndSetPersona(scenarioNum);
      setTranscript([]);
      setShowEval(false);
      setContinuedChat(false);
      setRestartKey(prev => prev + 1);
      setBranchPending(false);
    };

    const handleEvaluate = async () => {
      setLoadingEval(true);
      let convoText = transcript.map(msg => 
        `${msg.from === 'user' ? roleplayName : (employee ? employee.name : 'Employee')}: ${msg.text}`
      ).join("\n\n");
      
      try {
        const res = await fetch(`${apiBase}/evaluate`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ convo: convoText, session_id: localStorage.getItem("session_id") }),
        });
        const data = await res.json();
        setEvaluationMarkdown(data.feedback || "No feedback available.");
        setShowEval(true);
      } catch (err) {
        setEvaluationMarkdown("⚠️ Network error: " + err.message);
        setShowEval(true);
      } finally {
        setLoadingEval(false);
      }
    };

    //add branching functions!
    const handleCreateBranch = async (transcriptIndex) => {
      const currentSessionId = localStorage.getItem("session_id");
      try {
        const res = await fetch(`${API_BASE_URL}/branch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            session_id: currentSessionId,
            transcript_index: transcriptIndex,
          }),
        });
        const data = await res.json();

        if (!res.ok || !data.new_session_id) {
          throw new Error(data.error || `Branch request failed (${res.status})`);
        }
        setBranchPending(true);
        localStorage.setItem("session_id", data.new_session_id);
        setTranscript(transcript.slice(0, transcriptIndex));
        setRestartKey(prev => prev + 1);
      } catch (err) {
        console.error("Branch failed:", err);
        alert("Could not retry from this point. Please try again.");
      }
    };

    if (!employee) {
      return (
        <div style={{...styles.page, background: themeColors.background}}>
          <div style={{...styles.topBar, background: styles.topBarBg(theme), borderBottom: `1px solid ${themeColors.borderColor}`}}>
            <BrandWordmark theme={theme} compact={isMobile} />
          </div>
          <div style={styles.container}>
            <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
          </div>
        </div>
      );
    }

    return (
      <>
        <style>{scrollbarStyles}</style>
        <style>{spinnerKeyframes}</style>
        {showScenarioDropdown && (
          <ScenarioDropdown 
            onScenarioSelected={handleScenarioSelect}
            theme={theme}
            dropdownRef={dropdownRef}
            apiBase={apiBase}
          />
        )}
        
        {showEval ? (
          <EvaluationPage 
            isMobile={isMobile}
            transcript={transcript}
            roleplayName={roleplayName}
            onReturnToChat={handleReturnToChat}
            onRestartScenario={handleRestartScenario}
            onNewScenario={toggleScenarioDropdown}
            evaluationMarkdown={evaluationMarkdown}
            employee={employee}
            theme={theme}
            setTheme={setTheme}
          />
        ) : (
          <>
            {modalView === 'persona' && (
              <Modal 
                onClose={() => setModalView(null)}
                title={employee.name}
                subtitle={`${employee.role} ${employee.dept && "•"} ${employee.dept}`}
                theme={theme}
              >
                <PersonaPanel employee={employee} theme={theme} mode={mode} />
              </Modal>
            )}

            {modalView === 'scenario' && isMobile && (
              <Modal 
                onClose={() => setModalView(null)}
                theme={theme}
                showHeader={false}
              >
                <EmployeePanel employee={employee} roleplayName={roleplayName} theme={theme} onClose={() => setModalView(null)} mode={mode} />
              </Modal>
            )}

          <div style={{...styles.page, background: getThemeColors(theme).background}}>
            <TopBar
              isMobile={isMobile}
              onToggleView={(view) => setModalView(view)}
              employeeName={employee.scenario_name_full}
              roleplayName={roleplayName}
              onEvaluate={handleEvaluate}
              employee={employee}
              onRestartScenario={handleRestartScenario}
              onNewScenario={toggleScenarioDropdown}
              onShowPersona={() => setModalView('persona')}
              theme={theme}
              setTheme={setTheme}
              loadingEval={loadingEval}
              onMyAccount={() => navigate('/userdashboard')}
            />
            {!isMobile && (
              <div style={{
                padding: '8px 24px',
                fontSize: 13,
                color: themeColors.textMuted,
                textAlign: 'center',
              }}>
                Welcome, {localStorage.getItem('username')}! You have {remainingTurns} monthly messages remaining.
              </div>
            )}
            <div style={{...styles.container, padding: isMobile ? 12 : 0}}>
              <div style={{ 
                  ...styles.grid, 
                  gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden'
              }}>
                <div style={{ 
                  height: isMobile ? `calc(80vh - ${evaluateButtonHeight}px)` : '78vh',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden' 
                }}>
                  <ChatPane 
                    key={restartKey}
                    onMessagesChange={handleTranscriptUpdate} 
                    onShowPersona={() => setModalView('persona')} 
                    employee={employee}
                    initialMessages={transcript}
                    theme={theme}
                    isMobile={isMobile}
                    mobileButtonHeight={evaluateButtonHeight}
                    continuedChat={continuedChat}
                    setRemainingTurns={setRemainingTurns}
                    onCreateBranch={handleCreateBranch}
                    branchPending={branchPending}
                    onBranchConsumed={() => setBranchPending(false)}
                  />
                </div>
                {!isMobile && (
                  <div style={{ height: '78vh', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexShrink: 0 }}>
                      <button 
                        onClick={() => setDesktopView('scenario')}
                        style={{ 
                          ...styles.sendBtn, 
                          flex: 1,
                          padding: '8px 12px',
                          background: desktopView === 'scenario' ? BRAND.accent : themeColors.buttonInactive,
                          color: desktopView === 'scenario' ? contrastText(BRAND.accent) : contrastText(themeColors.buttonInactive)
                        }}
                      >
                        Scenario
                      </button>
                      <button 
                        onClick={() => setDesktopView('persona')}
                        style={{ 
                          ...styles.sendBtn, 
                          flex: 1,
                          padding: '8px 12px',
                          background: desktopView === 'persona' ? BRAND.accent : themeColors.buttonInactive,
                          color: desktopView === 'persona' ? contrastText(BRAND.accent) : contrastText(themeColors.buttonInactive)
                        }}
                      >
                        Persona
                      </button>
                    </div>
                    
                    {desktopView === 'scenario' ? (
                      <div style={{ 
                        ...styles.card(themeColors),
                        padding: 0,
                        color: themeColors.text,
                        maxHeight: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        <EmployeePanel employee={employee} roleplayName={roleplayName} theme={theme} mode={mode} />
                      </div>
                    ) : (
                    <div style={{ 
                      ...styles.card(themeColors),
                      padding: 0,
                      maxHeight: '100%',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <div style={{ 
                        padding: '16px', 
                        borderBottom: `1px solid ${themeColors.borderColor}`,
                        flexShrink: 0
                      }}>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: themeColors.text }}>
                          {employee.name}
                        </h2>
                        <div style={{ fontSize: 13, color: themeColors.textMuted, marginTop: 6 }}>
                          {employee.role} {employee.dept && "•"} {employee.dept}
                        </div>
                      </div>
                      <div className="custom-scrollbar" style={{ 
                        padding: '16px',
                        overflowY: 'auto',
                        flex: 1
                      }}>
                        <PersonaPanel employee={employee} theme={theme} mode={mode} />
                      </div>
                    </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {isMobile && (
              <div ref={evaluateButtonRef} style={{ 
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '12px 20px', 
                borderTop: `1px solid ${getThemeColors(theme).borderColor}`,
                background: getThemeColors(theme).background
              }}>
                <button 
                  style={{ ...styles.sendBtn, width: '100%', cursor: loadingEval ? 'wait' : 'pointer' }} 
                  onClick={handleEvaluate}
                  disabled={loadingEval}
                >
                  {loadingEval && <Spinner />}
                  {loadingEval ? 'Evaluating...' : 'Submit and Evaluate'}
                </button>
              </div>
            )}
            
            {!isMobile && (
              <div style={{
                ...styles.footer, 
                color: getThemeColors(theme).textMuted,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>© {new Date().getFullYear()} Lausanne Business Solutions — Loopen. All rights reserved.</span>
                <ThemeToggle theme={theme} setTheme={setTheme} />
              </div>
            )}
          </div>
        </>
        )}
      </>
    );
  }

  export default function LoopenUI() {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const isMobile = useIsMobile();
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

    useEffect(() => {
      localStorage.setItem('theme', theme);
      document.documentElement.setAttribute('data-theme', theme);
      document.body.style.backgroundColor = routerLocation.pathname === '/'
        ? LANDING_BG
        : getThemeColors(theme).background;
    }, [theme, routerLocation.pathname]);


    const handleCustomScenarioSubmit = async (customScenario) => {
        try {
            const res = await fetch(`${API_BASE_URL}/custom-scenarios`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(customScenario)
            });

            if (res.status === 401) {
            navigate("/login");            // or setShowLanding(true) / open login modal
            return;                        // stop here
            };
            const data = await res.json();
            if (data.success) {
                localStorage.setItem('selected_scenario_num', -1);
                navigate('/app');
            } else {
                console.error("Save failed:", data.error);
            }
        } catch (err) {
            console.error("Error saving custom scenario:", err);
        }
    };

    return(
      <Routes>
        <Route path="/" element={
          <LandingPage
            onSignIn={() => navigate('/login')}
            onSignUp={() => navigate('/register')}
            styles={styles}
            theme={theme}
            isMobile={isMobile}
            API_BASE_URL={API_BASE_URL}
          />
        } />
        <Route path="/register" element={
          <RegistrationPage
              theme={theme}
          />
        } />
        <Route path="/login" element={
          <AuthScreen theme={theme} setTheme={setTheme} />
        } />
        <Route path="/verify_email" element={
          <VerifyEmail theme={theme} />
        } />
        <Route path="/reset_password" element={
          <ResetPassword theme={theme} />
        } />
        <Route path="/userdashboard" element={
          <UserDashboard theme={theme} setTheme={setTheme} />
        } />
        <Route path="/custom-scenario" element={
          <CustomScenarioPage
            onBack={() => navigate('/userdashboard')}
            onSubmit={handleCustomScenarioSubmit}
            theme={theme}
            setTheme={setTheme}
          />
        } />
        <Route path="/transcripts" element={
          <TranscriptPage theme={theme} setTheme={setTheme} />
        } />
        <Route path="/mentoring-app" element={
          <AppShell 
            theme={theme} 
            setTheme={setTheme} 
            apiBase={`${API_BASE_URL}/mentoring`}
            storageKey="mentoring_scenario_num"
            mode="mentoring"
          />
        } />
        <Route path="/app" element={
          <AppShell theme={theme} setTheme={setTheme} />
        } />
      </Routes>
    );
  }